import React from 'react';
import styled from 'styled-components';

import formatSize from '../../../../utils/formatSize';
import { DOT_OPERATOR } from '../../../../constants/symbols';

import { Close } from '@icons/common';
import { Attachment } from '@icons/editor';

import Bubble from '../../../Bubble';
import { InnerWrapper } from '../../../Bubble/Bubble';

const IMAGE_URL_REGEXP = /\.(png|jpe?g|gif|webp|bmp|svg)(?:[?#]|$)/i;

const isImageMimeType = (type?: string | null) =>
  Boolean(type?.toLowerCase().startsWith('image/'));

// Параметры для удаления уже загруженного вложения
interface IUploadedAttachDeleteParams {
  attachId: string;
}

// Параметры для удаления локально добавленного вложения
interface ILocalAttachDeleteParams {
  name: string;
  lastModified: number;
}

interface IAttachmentList2 {
  className?: string;
  list: IAttachmentItem[];
  isWithPreview?: boolean;
  onDelete?:
    | ((params: {
        attachId?: string;
        name?: string;
        lastModified?: number;
      }) => void)
    | null;
}

interface IAttachmentItem {
  id?: string;
  name: string;
  size: number;
  type?: string;
  url?: string;
  lastModified?: number;
  progress?: number | string;
  data?: any;
  isWithPreview?: boolean;
  onDelete?: IAttachmentList2['onDelete'];
}

const AttachmentList2 = React.memo(
  ({
    list,
    onDelete = null,
    className = '',
    isWithPreview = false,
  }: IAttachmentList2) => {
    const renderExtras = React.useCallback(
      url => <img width="200" height="auto" src={url} />,
      []
    );

    const AttachmentItem = React.memo(
      ({ data, isWithPreview, onDelete }: IAttachmentItem) => {
        const { name, progress, size, type, url, lastModified, id } = data;
        const [isImage, setIsImage] = React.useState(
          isImageMimeType(type) || Boolean(url && IMAGE_URL_REGEXP.test(url))
        );

        React.useEffect(() => {
          if (
            !url ||
            isImageMimeType(type) ||
            IMAGE_URL_REGEXP.test(url) ||
            typeof Image === 'undefined'
          ) {
            return undefined;
          }

          let isMounted = true;
          const image = new Image();

          image.onload = () => {
            if (isMounted) setIsImage(true);
          };
          image.onerror = () => {
            if (isMounted) setIsImage(false);
          };
          image.src = url;

          return () => {
            isMounted = false;
            image.onload = null;
            image.onerror = null;
          };
        }, [type, url]);

        const shouldShowPreview = isWithPreview && isImage;

        const handleDelete = () => {
          if (!onDelete) return;

          if (id && url) {
            onDelete({ attachId: id });
            return;
          }

          onDelete({ name, lastModified });
        };

        return (
          <StyledElement $url={url} key={`${lastModified}-${name}`}>
            <StyledProgressBar $progress={progress} />
            <StyledContent $progress={progress}>
              {typeof progress !== 'number' && !shouldShowPreview && (
                <StyledAttachmentIcon />
              )}
              {shouldShowPreview && (
                <StyledPreviewWrapper>
                  <StyledPreview
                    src={url ?? URL.createObjectURL(data)}
                    alt=""
                  />
                </StyledPreviewWrapper>
              )}
              {url ? (
                <StyledBubble
                  text=" "
                  renderExtras={() => renderExtras(url)}
                  disabled={!isImage}
                >
                  <StyledLink href={url} target="_blank">
                    {name || 'Вложение'}
                  </StyledLink>
                </StyledBubble>
              ) : (
                <StyledName>{name}</StyledName>
              )}
              {!!size && <StyledSize>{formatSize(size)}</StyledSize>}
              {onDelete && <StyledDeleteIcon onClick={handleDelete} />}
            </StyledContent>
          </StyledElement>
        );
      }
    );

    return (
      <ul className={className}>
        {list.map(attachment => (
          <AttachmentItem
            key={`${attachment.lastModified}-${attachment.name}`}
            {...attachment}
            data={attachment}
            isWithPreview={isWithPreview}
            onDelete={onDelete}
          />
        ))}
      </ul>
    );
  }
);

AttachmentList2.displayName = 'AttachmentList2';

const StyledPreview = styled.img`
  width: 100%;
  height: 100%;
`;

const StyledPreviewWrapper = styled.div`
  margin-right: 4px;
  max-width: 50px;
  height: 30px;
`;

export const StyledElement = styled.li<{ $url?: string }>`
  position: relative;
  align-items: center;
  color: ${({ $url }) => ($url ? '#007aff' : '#4d4d4d')};
  display: flex;
  font-size: 12px;
  line-height: 19px;

  & + & {
    margin-top: 2px;
  }
`;

const StyledProgressBar = styled.div<{ $progress?: number | string }>`
  display: flex;
  position: absolute;
  border-radius: 13px;
  height: 100%;
  width: ${({ $progress }) =>
    $progress ? `${Math.min(Number($progress), 100)}%` : '0%'};
  background-color: #dcedff;
`;

const StyledContent = styled.div<{ $progress?: number | string }>`
  z-index: 1;
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: center;
  padding: ${({ $progress }) =>
    typeof $progress === 'number' ? '5px 10px' : ''};
`;

const StyledAttachmentIcon = styled(Attachment)`
  width: 18px;
  height: 16px;
  min-width: 17px;
  min-height: 16px;
  margin-right: 4px;

  path {
    stroke: #007aff;
  }
`;

export const StyledDeleteIcon = styled(Close)`
  cursor: pointer;
  stroke: #4d4d4d;
  width: 10px;
  height: 10px;
  margin-left: auto;
`;

const StyledLink = styled.a<{ href: string }>`
  display: block;
  color: ${({ href }) => (href ? '#007aff' : '#4d4d4d')};
  line-height: 1.36;
  font-size: inherit;
  text-decoration: none;
  max-width: 100%;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  :hover {
    text-decoration: underline;
  }
`;

const StyledBubble = styled(Bubble)`
  min-width: 0;
  flex: 1;

  ${InnerWrapper} {
    min-width: 0;
    width: 100%;
  }
`;

const StyledName = styled.span`
  color: inherit;
  font-size: inherit;
  max-width: 80%;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const StyledSize = styled.span`
  font-size: inherit;
  white-space: nowrap;
  margin-left: 3px;
  margin-right: 5px;

  :before {
    content: '${DOT_OPERATOR}';
    margin-right: 3px;
  }
`;

export default AttachmentList2;
